import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight } from 'lucide-react';

const VIDEOS = [
  {
    src: 'https://videos.pexels.com/video-files/7064944/7064944-uhd_2560_1440_30fps.mp4',
    label: { it: 'Nord Italia — Laghi Alpini', en: 'Northern Italy — Alpine Lakes', es: 'Norte de Italia — Lagos Alpinos', pt: 'Norte da Italia — Lagos Alpinos' },
    poster: 'https://images.pexels.com/videos/7064944/aerial-view-alps-architecture-beautiful-7064944.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    src: 'https://videos.pexels.com/video-files/5910575/5910575-hd_1920_1080_30fps.mp4',
    label: { it: 'Toscana — Colline e Borghi', en: 'Tuscany — Hills & Villages', es: 'Toscana — Colinas y Pueblos', pt: 'Toscana — Colinas e Aldeias' },
    poster: 'https://images.pexels.com/videos/5910575/europe-italy-landscape-panorama-5910575.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    src: 'https://videos.pexels.com/video-files/34797490/14753388_2560_1440_30fps.mp4',
    label: { it: 'Civita di Bagnoregio — Tramonto', en: 'Civita di Bagnoregio — Sunset', es: 'Civita di Bagnoregio — Atardecer', pt: 'Civita di Bagnoregio — Por do Sol' },
    poster: 'https://images.pexels.com/videos/34797490/4k-landscape-4k-timelapse-castle-historic-italy-34797490.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    src: 'https://videos.pexels.com/video-files/15543962/15543962-hd_1920_1080_25fps.mp4',
    label: { it: 'Firenze — Il Duomo al Crepuscolo', en: 'Florence — The Duomo at Dusk', es: 'Florencia — El Duomo al Atardecer', pt: 'Florenca — O Duomo ao Entardecer' },
    poster: 'https://images.pexels.com/videos/15543962/basilica-cathedral-catholic-church-church-15543962.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
  {
    src: 'https://videos.pexels.com/video-files/15437345/15437345-uhd_2560_1440_24fps.mp4',
    label: { it: 'Costiera Amalfitana — Sud Italia', en: 'Amalfi Coast — Southern Italy', es: 'Costa Amalfitana — Sur de Italia', pt: 'Costa Amalfitana — Sul da Italia' },
    poster: 'https://images.pexels.com/videos/15437345/pexels-photo-15437345.jpeg?auto=compress&cs=tinysrgb&w=1280',
  },
];

const DISPLAY_DURATION = 8000;
const FADE_MS = 2000;

const CinematicVideoSection = () => {
  const { language } = useLanguage();
  const [showA, setShowA] = useState(true);
  const [idxA, setIdxA] = useState(0);
  const [idxB, setIdxB] = useState(1);
  const [displayIdx, setDisplayIdx] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const videoARef = useRef(null);
  const videoBRef = useRef(null);
  const timerRef = useRef(null);

  // Intersection Observer — lazy load
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Play the front video when visible or when it swaps
  useEffect(() => {
    if (!isVisible) return;
    const frontRef = showA ? videoARef : videoBRef;
    const backRef = showA ? videoBRef : videoARef;
    if (frontRef.current) { frontRef.current.currentTime = 0; frontRef.current.play().catch(() => {}); }
    if (backRef.current) { backRef.current.pause(); }
  }, [showA, isVisible]);

  const advance = useCallback(() => {
    setShowA(prev => {
      const goingToB = prev;
      if (goingToB) {
        // B is about to become front — preload next onto A
        const nextForA = (idxB + 1) % VIDEOS.length;
        setIdxA(nextForA);
        setDisplayIdx(idxB);
      } else {
        const nextForB = (idxA + 1) % VIDEOS.length;
        setIdxB(nextForB);
        setDisplayIdx(idxA);
      }
      return !prev;
    });
  }, [idxA, idxB]);

  // Auto-advance
  useEffect(() => {
    if (!isVisible) return;
    timerRef.current = setInterval(advance, DISPLAY_DURATION);
    return () => clearInterval(timerRef.current);
  }, [isVisible, advance]);

  const jumpTo = (i) => {
    clearInterval(timerRef.current);
    if (showA) { setIdxB(i); } else { setIdxA(i); }
    setDisplayIdx(i);
    setShowA(prev => !prev);
    timerRef.current = setInterval(advance, DISPLAY_DURATION);
  };

  const currentLabel = VIDEOS[displayIdx]?.label[language] || VIDEOS[displayIdx]?.label.en;
  const text = {
    it: { line1: 'Scopri le Tue Radici Italiane', line2: 'Dove ogni storia inizia… e ogni viaggio ti riporta a casa.' },
    en: { line1: 'Discover Your Italian Roots', line2: 'Where every story begins… and every journey leads you home.' },
    es: { line1: 'Descubre Tus Raices Italianas', line2: 'Donde cada historia comienza… y cada viaje te lleva a casa.' },
    pt: { line1: 'Descubra Suas Raizes Italianas', line2: 'Onde cada historia comeca… e cada jornada te leva para casa.' },
  }[language] || { line1: 'Discover Your Italian Roots', line2: 'Where every story begins… and every journey leads you home.' };

  const videoStyle = (isFront) => ({
    position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
    opacity: isFront ? 1 : 0,
    transition: `opacity ${FADE_MS}ms ease-in-out`,
    zIndex: isFront ? 2 : 1,
  });

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ height: '70vh', minHeight: '450px', maxHeight: '700px' }}
      data-testid="cinematic-video-section"
    >
      {/* Poster fallback */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${VIDEOS[0].poster})`, zIndex: 0 }} />

      {isVisible && (
        <>
          <video ref={videoARef} style={videoStyle(showA)} src={VIDEOS[idxA].src} poster={VIDEOS[idxA].poster} muted playsInline preload="auto" />
          <video ref={videoBRef} style={videoStyle(!showA)} src={VIDEOS[idxB].src} poster={VIDEOS[idxB].poster} muted playsInline preload="auto" />
        </>
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0" style={{ zIndex: 10, background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.65) 100%)' }} />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" style={{ zIndex: 20 }}>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg"
          style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.02em', lineHeight: 1.15 }}
          data-testid="cinematic-title"
        >
          {text.line1}
        </h2>
        <p
          className="text-lg sm:text-xl lg:text-2xl text-white/90 italic max-w-2xl drop-shadow-md"
          style={{ fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.5 }}
          data-testid="cinematic-subtitle"
        >
          {text.line2}
        </p>

        {/* CTA */}
        <Link
          to="/contact"
          className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full text-base font-semibold text-white border border-white/40 backdrop-blur-sm hover:bg-white hover:text-gray-900 transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.12)' }}
          data-testid="cinematic-cta"
        >
          {language === 'it' ? 'Inizia il Tuo Viaggio' : language === 'es' ? 'Comienza Tu Viaje' : language === 'pt' ? 'Comece Sua Jornada' : 'Start Your Journey'}
          <ArrowRight className="w-4 h-4" />
        </Link>

        {/* Location label */}
        <div className="mt-8 px-5 py-2 rounded-full border border-white/30 backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <span className="text-sm sm:text-base text-white/80 tracking-widest uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {currentLabel}
          </span>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2.5 mt-6" data-testid="video-progress-dots">
          {VIDEOS.map((_, i) => (
            <button key={i} onClick={() => jumpTo(i)} className="group" aria-label={`Video ${i + 1}`}>
              <span
                className="block rounded-full transition-all duration-500"
                style={{
                  width: i === displayIdx ? '28px' : '8px',
                  height: '8px',
                  background: i === displayIdx ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)',
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CinematicVideoSection;
